import { Alert, Link, Stack } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState } from 'react';
import GracePeriodApi from '../../../api/GracePeriodApi';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData } from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { dayjs } from '../../../date-util';
import { GracePeriodUpdateRequest } from '../../../types/gracePeriod';
import {
	GracePeriodResponse,
	PolicyCharacteristics,
} from '../../../types/policy';
import { getActiveCharacteristics, getDateErrors } from '../../../util/util';
import DefaultDialog, { Action } from '../../DefaultDialog';
import DefaultDatePicker, {
	DefaultDatePickerValue,
} from '../../form/DefaultDatePicker';
import { successToast } from '../../Toast';

interface Props {
	authData: AuthData;
	errorData: ErrorData;
	policyData: PolicyData;
	gracePeriod: GracePeriodResponse;
}

const useStyles = makeStyles((theme) => ({
	alert: {
		marginTop: theme.spacing(1),
	},
}));

const GracePeriodUpdateModalTrigger = (props: Props) => {
	const { authData, errorData, policyData, gracePeriod } = props;
	const classes = useStyles();

	const endTimestampDate = gracePeriod.endTimestamp
		? new Date(parseInt(gracePeriod.endTimestamp, 10))
		: null;
	const cancelTimestampDate = gracePeriod.cancelEffectiveTimestamp
		? new Date(parseInt(gracePeriod.cancelEffectiveTimestamp, 10))
		: null;
	const [newEndDate, setNewEndDate] =
		useState<DefaultDatePickerValue>(endTimestampDate);
	const [cancellationDate, setCancellationDate] =
		useState<DefaultDatePickerValue>(cancelTimestampDate);
	const [loading, setLoading] = useState(false);
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setNewEndDate(endTimestampDate);
			setCancellationDate(cancelTimestampDate);
		}
	}, [gracePeriod.endTimestamp, gracePeriod.cancelEffectiveTimestamp, isOpen]);

	if (!gracePeriod) {
		return null;
	}

	const isSettled = !!gracePeriod.settledTimestamp;
	const isActive =
		!isSettled &&
		Date.now() >= parseInt(gracePeriod.startTimestamp, 10) &&
		Date.now() < parseInt(gracePeriod.endTimestamp, 10);

	if (!isActive) {
		return null;
	}

	const { locator: policyLocator, characteristics } = policyData.policy;
	const { policyStartTimestamp, policyEndTimestamp } =
		(getActiveCharacteristics(characteristics)?.[0] as PolicyCharacteristics) ||
		{};

	const close = () => setIsOpen(false);
	const { authorizationToken } = authData.auth;

	const minEndDate = dayjs().add(1, 'day').startOf('day').toDate();
	const minCancellationDate = policyStartTimestamp
		? dayjs(parseInt(policyStartTimestamp)).startOf('day').toDate()
		: null;
	const maxCancellationDate = policyEndTimestamp
		? dayjs(parseInt(policyEndTimestamp))
				.subtract(1, 'day')
				.startOf('day')
				.toDate()
		: null;
	const endDateErrors = getDateErrors(
		newEndDate,
		minEndDate,
		null,
		true,
		authData,
	);
	const cancellationDateErrors = getDateErrors(
		cancellationDate,
		minCancellationDate,
		maxCancellationDate,
		false,
		authData,
	);
	const isFormValid = endDateErrors.isValid && cancellationDateErrors.isValid;

	const handleConfirm = async () => {
		if (!isFormValid) {
			return;
		}

		const params: GracePeriodUpdateRequest = {};

		if (dayjs(newEndDate as Date).isValid()) {
			params.endTimestamp = `${(newEndDate as Date).getTime()}`;
		}

		if (dayjs(cancellationDate as Date).isValid()) {
			params.cancelEffectiveTimestamp = `${(
				cancellationDate as Date
			).getTime()}`;
		} else if (cancellationDate === '') {
			params.resetCancelEffectiveTimestamp = true;
		}

		setLoading(true);

		try {
			await GracePeriodApi.updateGracePeriod(
				authorizationToken,
				params,
				gracePeriod.locator,
			);
			await policyData.loadData(policyLocator);
			closeAndClear();
			successToast('Successfully updated grace period');
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to update grace period');
			await handleUnauthorizedResponse(error, authData);
			closeAndClear();
		}
	};

	const closeAndClear = () => {
		close();
		setLoading(false);
	};

	const confirmDisabled =
		loading ||
		!isFormValid ||
		(endTimestampDate?.getTime() === (newEndDate as Date)?.getTime() &&
			(!!cancellationDate
				? cancelTimestampDate?.getTime() ===
				  (cancellationDate as Date)?.getTime()
				: cancelTimestampDate === cancellationDate)); // disable confirm if all values in form are the same.

	const actions: Action[] = [
		{
			text: 'Cancel',
			disabled: loading,
			onClick: closeAndClear,
			color: 'neutralButton',
			variant: 'text',
		},
		{
			text: 'Confirm',
			onClick: handleConfirm,
			color: 'secondary',
			disabled: confirmDisabled,
		},
	];

	return (
		<>
			<Link onClick={() => setIsOpen(true)}>Edit</Link>
			<DefaultDialog
				actions={actions}
				open={isOpen}
				onClose={close}
				title='Edit Grace Period'
			>
				<Stack spacing={2}>
					<DefaultDatePicker
						id='start-timestamp'
						label='Start Date'
						fullWidth
						disabled
						value={new Date(parseInt(gracePeriod.startTimestamp, 10))}
						onChange={null}
					/>
					<DefaultDatePicker
						id='end-timestamp'
						label='Lapse Date'
						required
						showCalendar
						fullWidth
						error={!endDateErrors.isValid}
						errorMessage={!endDateErrors.isValid ? endDateErrors.message : null}
						value={newEndDate}
						onChange={(date) => setNewEndDate(date)}
						minDate={minEndDate}
					/>
					<DefaultDatePicker
						id='cancel-effective-timestamp'
						label='Override Cancellation Date'
						showCalendar
						fullWidth
						error={!cancellationDateErrors.isValid}
						errorMessage={
							!cancellationDateErrors.isValid
								? cancellationDateErrors.message
								: null
						}
						value={cancellationDate}
						onChange={(date) => {
							setCancellationDate(date);
						}}
						minDate={minCancellationDate}
						maxDate={maxCancellationDate}
					/>
				</Stack>
				<Alert className={classes.alert} severity='warning'>
					Are you sure you want to update when the policy will lapse?
				</Alert>
			</DefaultDialog>
		</>
	);
};

export default withPolicyContext<Props>(GracePeriodUpdateModalTrigger);
